import { Card, CardBody, CardTitle } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { withNamespaces } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import ComplianceMonitoringDashboardService from "src/modules/3rd-party-management/apis/admin/compliance/monitoring/DashboardService";
import { toast } from "react-toastify";
import ChartError from "src/modules/3rd-party-management/pages/admin/compliance/components/dashboard/ChartError";

const sectionConfigs = {
    false_positive: {
        title: "False Positive",
        color: "#5664D2"
    },
    true_positive: {
        title: "True Positive",
        color: "#1CBB8C"
    },
    unknown: {
        title: "Not Specified",
        color: "#D9D9D9"
    }
}

const AccuracyAssessment = ({ t, filters }) => {
    const fetchDataQuery = useQuery({
        queryKey: [
            "3rd-party-management-fetch-compliance-monitoring-dashboard",
            "accuracy-assessment",
            filters
        ],
        queryFn: async () => {
            const service = ComplianceMonitoringDashboardService.getInstance();

            return await service.fetchAccuracyAssessment(filters);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t("An error occurred while fetching dashboard data."), {
                type: "error"
            });
        },
    });

    if (fetchDataQuery.isFetching) {
        return (
            <Card>
                <CardBody>
                    <div className="dt-field dt-skeleton mb-2" style={{ width: '40%' }}></div>
                    <div className="dt-field dt-skeleton" style={{ width: '100%', height: '320px' }}></div>
                </CardBody>
            </Card>
        );
    }

    const {
        segments
    } = fetchDataQuery.data || {};

    const options = {
        labels: [],
        plotOptions: {
            pie: {
                donut: {
                    size: "75%"
                }
            }
        },
        dataLabels: {
            enabled: true
        },
        legend: {
            show: true,
            position: 'bottom'
        },
        colors: []
    };

    const series = [];
    const labels = [];
    const colors = [];

    segments.forEach(segment => {
        series.push(segment.count || 0);
        labels.push(t(sectionConfigs[segment.key]?.title || segment.key));
        colors.push(sectionConfigs[segment.key]?.color || "#000000");
    });

    const isSeriesEmpty = series.every((s) => s === 0)

    return (
        <Card className="h-100">
            <CardBody>
                <div className="mb-4">
                    <CardTitle>
                        <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}>
                            <div>
                                {t('Accuracy Assessment')}
                            </div>
                        </div>
                    </CardTitle>
                </div>

                <div className="mb-4">
                    <div className="apex-charts mb-3">
                        {fetchDataQuery.isError ? (
                            <ChartError
                                title={
                                    fetchDataQuery.error?.response?.status === 403 ?
                                        'Error 403' :
                                        null
                                }
                                message={
                                    fetchDataQuery.error?.response?.status === 403 ?
                                        'Please upgrade your plan to access this feature' :
                                        null
                                }
                                onRetry={fetchDataQuery.refetch}
                            />
                        ) : (
                            <ReactApexChart
                                options={{
                                    ...options,
                                    labels: isSeriesEmpty ? [t("No Data")] : labels,
                                    colors: isSeriesEmpty ? ["e0e0e0"] : colors
                                }}
                                series={isSeriesEmpty ? [1] : series}
                                type="donut"
                                height="280"
                            />
                        )}
                    </div>
                </div>
            </CardBody>
        </Card>
    )
};

export default withNamespaces()(
    AccuracyAssessment
);